import { z } from 'zod';
import {
  ALLOWED_CONTENT_TYPES,
  PERSONAL_NUMBER_REGEX,
} from '@sapling/constants/grants';

const MAX_TEXT_LENGTH = 2000 as const;

// --- Type guard ---

export function isAllowedContentType(
  value: string,
): value is z.infer<typeof uploadContentType> {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(value);
}

// --- Shared field schemas ---

const uploadContentType = z.enum(ALLOWED_CONTENT_TYPES);

// --- Form input schema ---

export const grantApplicationInputSchema = z.object({
  // Personal information
  name: z.string().min(1, { message: 'Du måste ange ditt namn.' }).max(200),
  personalNumber: z
    .string()
    .regex(PERSONAL_NUMBER_REGEX, {
      message: 'Personnummer måste anges i formatet ÅÅÅÅMMDD-XXXX.',
    })
    .optional()
    .or(z.literal('')),
  address: z.string().min(1, { message: 'Du måste ange din adress.' }).max(500),
  phone: z
    .string()
    .min(1, { message: 'Du måste ange ditt telefonnummer.' })
    .max(50),
  email: z.string().email({ message: 'Du måste ange en giltig e-postadress.' }),

  // About your situation
  aboutYourself: z
    .string()
    .min(10, {
      message: 'Beskriv din situation med minst 10 tecken.',
    })
    .max(MAX_TEXT_LENGTH),
  reasonForSupport: z
    .string()
    .min(10, {
      message: 'Förklara varför du söker stöd med minst 10 tecken.',
    })
    .max(MAX_TEXT_LENGTH),
  previousSupport: z
    .string()
    .min(1, {
      message: 'Du måste svara på om du sökt stöd tidigare.',
    })
    .max(MAX_TEXT_LENGTH),

  // What you are seeking support for
  fundUsage: z
    .string()
    .min(1, {
      message: 'Beskriv vad fondmedlen ska gå till.',
    })
    .max(MAX_TEXT_LENGTH),
  amount: z
    .number()
    .min(100, { message: 'Beloppet måste vara minst 1 kr.' })
    .max(500000, {
      message: 'Maxbeloppet är 5 000 kr per ansökan.',
    }),
  amountBasis: z
    .string()
    .min(1, {
      message: 'Ange hur du räknat ut beloppet.',
    })
    .max(MAX_TEXT_LENGTH),

  // Motivation
  whyImportant: z
    .string()
    .min(1, {
      message: 'Förklara varför stödet är viktigt för dig.',
    })
    .max(MAX_TEXT_LENGTH),
  shortAndLongTerm: z
    .string()
    .min(1, {
      message: 'Beskriv hur stödet skulle hjälpa dig.',
    })
    .max(MAX_TEXT_LENGTH),
  additionalInfo: z.string().max(MAX_TEXT_LENGTH).optional().or(z.literal('')),

  // Follow-up and responsibility
  howToVerify: z
    .string()
    .min(1, {
      message: 'Beskriv hur vi kan säkerställa att medlen används rätt.',
    })
    .max(MAX_TEXT_LENGTH),
  willingToFollowUp: z.boolean({
    errorMap: () => ({ message: 'Du måste svara ja eller nej.' }),
  }),

  // Consent
  certifyCorrectInfo: z.boolean().refine((val) => val === true, {
    message:
      'Du måste intyga att uppgifterna är korrekta och att medlen används enligt ansökan.',
  }),
  consentToProcessing: z.boolean().refine((val) => val === true, {
    message:
      'Du måste samtycka till att Stiftelsen Sapling behandlar dina personuppgifter.',
  }),

  // Attachments
  uploads: z
    .array(
      z.object({
        uploadId: z.string().ulid(),
        originalFileName: z.string().max(255),
        contentType: uploadContentType,
      }),
    )
    .optional()
    .default([]),

  // Idempotency
  submissionToken: z.string().uuid(),
});

export type GrantApplicationInput = z.infer<typeof grantApplicationInputSchema>;

// --- Cosmos DB entity ---

export const attachmentSchema = z.object({
  uploadId: z.string().ulid(),
  bucket: z.string(),
  s3Key: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
});

export type Attachment = z.infer<typeof attachmentSchema>;

export const grantApplicationStatus = z.enum([
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
  'NEEDS_INFO',
]);

export const grantApplicationEntitySchema = z.object({
  id: z.string(),
  partitionKey: z.string(),
  type: z.literal('grant-application'),
  referenceId: z.string(),

  name: z.string(),
  personalNumber: z.string().optional(),
  address: z.string(),
  phone: z.string(),
  email: z.string(),

  aboutYourself: z.string(),
  reasonForSupport: z.string(),
  previousSupport: z.string(),

  fundUsage: z.string(),
  amount: z.number(),
  amountBasis: z.string(),

  whyImportant: z.string(),
  shortAndLongTerm: z.string(),
  additionalInfo: z.string().optional(),

  howToVerify: z.string(),
  willingToFollowUp: z.boolean(),

  certifyCorrectInfo: z.boolean(),
  consentToProcessing: z.boolean(),

  attachments: z.array(attachmentSchema),

  submissionToken: z.string(),
  ipAddress: z.string().optional(),
  createdAt: z.string().datetime(),

  status: grantApplicationStatus.default('SUBMITTED'),
  reviewedBy: z.string().nullable().default(null),
  reviewedAt: z.string().datetime().nullable().default(null),

  /** Internal staff notes. Embedded in the document — bounded at review lifecycle scale. */
  notes: z
    .array(
      z.object({
        id: z.string(),
        text: z.string(),
        author: z.string(),
        createdAt: z.string().datetime(),
      }),
    )
    .default([]),

  /** Tracks status transitions for audit trail. */
  statusHistory: z
    .array(
      z.object({
        from: grantApplicationStatus,
        to: grantApplicationStatus,
        changedBy: z.string(),
        changedAt: z.string().datetime(),
      }),
    )
    .default([]),
});

export type GrantApplicationEntity = z.infer<
  typeof grantApplicationEntitySchema
>;
export type GrantApplicationStatus = z.infer<typeof grantApplicationStatus>;

// --- Response schemas (strip Cosmos/S3 internals) ---

const attachmentResponseSchema = attachmentSchema.omit({
  bucket: true,
  s3Key: true,
});

/** List view — only the fields needed for the table. */
export const grantApplicationListItemSchema = grantApplicationEntitySchema.pick(
  {
    id: true,
    referenceId: true,
    name: true,
    email: true,
    amount: true,
    status: true,
    createdAt: true,
  },
);

/** Detail view — all applicant-facing fields, no Cosmos/S3 internals. */
export const grantApplicationDetailSchema = grantApplicationEntitySchema
  .omit({
    partitionKey: true,
    submissionToken: true,
    ipAddress: true,
  })
  .extend({
    attachments: z.array(attachmentResponseSchema),
  });

export type GrantApplicationListItem = z.infer<
  typeof grantApplicationListItemSchema
>;
export type GrantApplicationDetail = z.infer<
  typeof grantApplicationDetailSchema
>;
